/* eslint-disable id-match */
/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';

import * as graphql from 'generated/graphql';

import Logout from 'packages/kakapo-ui-application/pages/auth/Logout';
import useAuthenticatedUser from '../hooks/useAuthenticatedUser';
import HelpdeskContainer from '../common/containers/HelpdeskContainer';

import Analytics from 'analytics/Analytics';
import AnalyticsV2 from 'analytics/v2/Analytics';
import Metric from 'analytics/v2/Metric';
import Conversations from 'conversations/Conversations';
import UserCompliments from 'feedback/UserCompliments';
import ViewerCompliments from 'feedback/ViewerCompliments';
import Goals from 'goals/Goals';
import Home from 'home/Home';
import Layout from 'layout/Layout';
import Notes from 'notes/Notes';
import Learning from 'learning/Learning';
import ViewerOutgoingFeedback from 'feedback/ViewerOutgoingFeedback';
import UserFeedback from 'feedback/UserFeedback';
import UserOutgoingFeedback from 'feedback/UserOutgoingFeedback';
import ViewerFeedback from 'feedback/ViewerFeedback';
import TalentReviewsV2 from 'talent-reviews/TalentReviews';
import TalentReview from 'talent-reviews/TalentReview';
import Compliment from 'feedback/Compliment';
import OutgoingFeedback from 'feedback/OutgoingFeedback';
import SharedFeedback from 'feedback/SharedFeedback';
import Login from '../pages/auth/Login';
import Settings from 'settings/Settings';
import Journey from 'journey/Journey';
import FeedbackResults from 'feedback/results/FeedbackResults';
import DefaultVersionSwitch from 'navigation/DefaultVersionSwitch';
import FeedbackResponseComposer from 'feedback/FeedbackResponseComposer';
import PersonalFiles from 'personal-files/PersonalFiles';
import PersonalSettings from 'personal-settings/PersonalSettings';
import ViewerCompetencies from 'competencies/ViewerCompetencies';
import Manager from '../../../manager/Manager';
import UserCompetencies from 'competencies/UserCompetencies';
import JobPosition from 'job/JobPosition';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import CompetencySearch, { CompetencySearchStart } from 'competencies/CompetencySearch';
import DevelopmentPlanRoute from 'development-plan/DevelopmentPlanRoute';
import EngagementReplies from 'engagement/replies/EngagementReplies';

function ViewerRedirect() {
	const history = useHistory();

	useEffect(() => {
		const destination = history.location.pathname.replace(/^\/v2\/users\/[^/]+/, `/v2`);
		history.replace({ pathname: destination, search: history.location.search });
	}, []);

	return null;
}

function UserProfileRedirect(props: { userID: string }) {
	const query = graphql.useUserQuery({ variables: { id: props.userID } });

	if (!query.data) {
		return null;
	}

	if (query.data.user.viewerCanViewJourney) {
		return <Redirect to={`/v2/users/${props.userID}/journey`} />;
	}

	return <Redirect to={`/v2/users/${props.userID}/notes`} />;
}

export function MainRoutes() {
	const currentUser = useAuthenticatedUser();

	return (
		<Switch>
			<Route
				path={[
					'/v2',
					'/v2/journey',
					'/v2/journey/jobs',
					'/v2/journey/learning/:id',
					'/v2/inbox/manager',
				]}
				exact
			>
				<Home />
			</Route>
			<Route path="/v2/manager">
				<Manager />
			</Route>
			<Route path="/v2/personal-settings">
				<PersonalSettings />
			</Route>
			<Route path={['/v2/personal-files', '/personal-files']}>
				<PersonalFiles userID={currentUser.id} />
			</Route>
			<Route path="/v2/analytics/v2" exact>
				<AnalyticsV2 />
			</Route>
			<Route path="/v2/analytics/v2/metric/:metricGroupID/:metricID/:tabID?">
				<Metric />
			</Route>
			<Route path="/v2/analytics">
				<Analytics />
			</Route>
			<Route
				path={['/v2/engagement-replies/:answerManagerID', '/engagement-replies/:answerManagerID']}
				exact
			>
				{({ match }) =>
					!!match && <EngagementReplies answerManagerID={match!.params.answerManagerID} />
				}
			</Route>
			<Route path={`/v2/users/${currentUser.id}`}>
				<ViewerRedirect />
			</Route>
			<Route path={['/v2/goals', '/v2/users/:id/goals']}>
				<Goals />
			</Route>
			<Route path={['/v2/users/:id', '/users/:id']} exact>
				{({ match }) => !!match && <UserProfileRedirect userID={match!.params.id} />}
			</Route>
			<Route path="/v2/users/:id/personal-files">
				{({ match }) => <PersonalFiles userID={match!.params.id} />}
			</Route>
			<Route path={['/v2/users/:id/journey']}>
				{({ match }) => <Journey userID={(match?.params as Record<string, string>)['id']} jumbo />}
			</Route>
			<Route exact path={['/v2/feedback/given/:id', '/v2/users/:userID/feedback/given/:id']}>
				<OutgoingFeedback />
			</Route>
			<Route exact path="/v2/feedback/given">
				<ViewerOutgoingFeedback />
			</Route>
			<Route exact path="/v2/feedback/shared">
				<SharedFeedback />
			</Route>
			<Route exact path="/v2/users/:id/feedback/given">
				<UserOutgoingFeedback />
			</Route>
			<Route
				exact
				path={['/v2/feedback/compliments', '/v2/feedback/compliments/new', '/feedback/compliments']}
			>
				<ViewerCompliments />
			</Route>
			<Route
				exact
				path={[
					'/v2/users/:id/feedback/compliments',
					'/v2/users/:id/feedback/compliments/new',
					'/v2/users/:id/feedback/compliments/thank-you',
					'/users/:id/feedback/compliments',
					'/users/:id/feedback/compliments/new',
					'/users/:id/feedback/compliments/thank-you',
				]}
			>
				<UserCompliments />
			</Route>
			<Route
				exact
				path={[
					'/v2/feedback/compliments/:id',
					'/feedback/compliments/:id',
					'/v2/users/:userID/feedback/compliments/:id',
					'/users/:userID/feedback/compliments/:id',
				]}
			>
				<Compliment />
			</Route>
			<Route
				path={[
					'/v2/feedback',
					'/v2/feedback/send',
					'/v2/feedback/request',
					'/v2/feedback/request-from-moment',
					'/v2/feedback/request-from-moment/:id',
					'/v2/feedback/request/competencies',
				]}
				exact
			>
				<ViewerFeedback />
			</Route>
			<Route
				path={['/v2/feedback/:requestID/respond', '/v2/users/:id/feedback/:requestID/respond']}
			>
				{({ match }) => <FeedbackResponseComposer requestID={match!.params.requestID} />}
			</Route>
			<Route
				path={[
					'/v2/users/:id/feedback',
					'/v2/users/:id/feedback/send',
					'/v2/users/:id/feedback/request',
					'/v2/users/:id/feedback/request-from-moment',
					'/v2/users/:id/feedback/request-from-moment/:momentID',
					'/v2/users/:id/feedback/request/competencies',
				]}
				exact
			>
				<UserFeedback />
			</Route>
			<Route
				path={[
					'/feedback/:requestID',
					'/users/:id/feedback/:requestID',
					'/v2/feedback/:requestID',
					'/v2/users/:id/feedback/:requestID',
				]}
			>
				{({ match }) => <FeedbackResults id={match!.params.requestID} />}
			</Route>
			<Route path="/v2/competencies/search" exact>
				<CompetencySearchStart />
			</Route>
			<Route path="/v2/competencies/search/:id">
				<CompetencySearch />
			</Route>
			<Route path="/v2/competencies/:jobTitleID">
				<ViewerCompetencies />
			</Route>
			<Redirect path="/v2/competencies" to="/v2/competencies/all" />
			<Route path="/v2/job-positions/:id">
				<JobPosition />
			</Route>
			<Route path="/v2/users/:id/competencies/:jobTitleID">
				<UserCompetencies />
			</Route>
			<Redirect path="/v2/users/:id/competencies" to="/v2/users/:id/competencies/all" />
			<Route path={['/v2/conversations', '/conversations', '/v2/users/:id/conversations']}>
				<Conversations />
			</Route>
			<Route path={['/v2/notes', '/v2/users/:id/notes']}>
				<Notes />
			</Route>
			<Route path={['/v2/development-plan']}>
				<DevelopmentPlanRoute />
			</Route>
			<Route path={['/v2/learning', '/v2/users/:id/learning']}>
				<Learning />
			</Route>
			<Route path="/v2/settings">
				<Settings />
			</Route>
			<Route exact path={['/v2/talent-reviews', '/v2/talent-reviews/(department|team)/:id/new']}>
				<TalentReviewsV2 />
			</Route>
			<Route path="/v2/talent-reviews/:id">
				{({ match }) => <TalentReview id={match!.params.id} />}
			</Route>
			<Route path="/v2/logout">
				<Logout />
			</Route>
		</Switch>
	);
}

function LaunchRedirect() {
	const workspace = useWorkspace();

	if (workspace.id !== 'launch') {
		return null;
	}

	return (
		<Switch>
			<Route path="/v2/settings/workspaces" />
			<Route>
				<Redirect to="/v2/settings/workspaces" />
			</Route>
		</Switch>
	);
}

function ScrollToTop() {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	return null;
}

export default function MainRouter() {
	return (
		<HelpdeskContainer>
			<DefaultVersionSwitch />
			<Switch>
				<Route path="/authentication">
					<Login />
				</Route>
				<Route path="/v2/authentication">
					<Login />
				</Route>
				<Route path="/v2">
					<Layout>
						<LaunchRedirect />
						<ScrollToTop />
						<MainRoutes />
					</Layout>
				</Route>
			</Switch>
		</HelpdeskContainer>
	);
}
